import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  Thermometer,
  AlertTriangle,
  ChevronRight,
  ShieldCheck,
  Flame,
} from "lucide-react";

type WindrowSummary = {
  id: number;
  row_id: string;
  name: string;
  status: string;
  last_temp: number | null;
  last_temp_at: string | null;
  compliance_days: number;
  needs_attention: boolean;
};

type OverviewData = {
  stats: {
    active_windrows: number;
    alert_count: number;
    avg_temp: number;
  };
  temp_alerts: Array<{
    row_id: string;
    name: string;
    temperature: number;
  }>;
};

export default function OperatorDashboard() {
  const { token } = useAuth();
  const [windrows, setWindrows] = useState<WindrowSummary[]>([]);
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiFetch("/api/windrows", { headers: { Authorization: `Bearer ${token}` } }).then((r) => (r.ok ? r.json() : null)),
      apiFetch("/api/operations/overview", { headers: { Authorization: `Bearer ${token}` } }).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([windrowData, overviewData]) => {
        const alerts = overviewData?.temp_alerts ?? [];
        const alertRowIds = new Set(
          alerts
            .filter((a: { temperature: number }) => a.temperature < 131)
            .map((a: { row_id: string }) => a.row_id)
        );

        const list = windrowData?.windrows ?? [];
        const active = list
          .filter((w: WindrowSummary) => w.status === "active")
          .map((w: WindrowSummary) => ({
            ...w,
            needs_attention: alertRowIds.has(w.row_id),
          }));

        setWindrows(active);
        setOverview(overviewData ?? null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
            <div className="h-4 bg-muted rounded w-32 mb-3" />
            <div className="h-8 bg-muted rounded w-20" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="heading-display text-2xl text-foreground">My Windrows</h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          {windrows.length} active row{windrows.length !== 1 ? "s" : ""}
          {overview?.stats.alert_count ? (
            <span className="text-red-500 font-semibold ml-1">
              · {overview.stats.alert_count} need attention
            </span>
          ) : null}
        </p>
      </div>

      {windrows.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <ShieldCheck size={32} className="mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">No active windrows</p>
        </div>
      ) : (
        <div className="space-y-3">
          {windrows.map((w) => (
            <Link
              key={w.id}
              to={`/app/operator/windrows/${w.id}`}
              className={`block bg-card border rounded-xl p-4 transition-all active:scale-[0.98] ${
                w.needs_attention
                  ? "border-red-200 dark:border-red-900 hover:border-red-300 dark:hover:border-red-800"
                  : "border-border hover:border-forest-300 dark:hover:border-forest-800"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-bold text-foreground truncate">{w.name}</h3>
                    {w.needs_attention && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-[10px] font-bold uppercase tracking-wide rounded-full shrink-0">
                        <AlertTriangle size={10} />
                        Attention
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">{w.row_id}</p>
                </div>

                <div className="flex items-center gap-4 shrink-0">
                  {/* Last temp — big */}
                  <div className="text-right">
                    {w.last_temp !== null ? (
                      <>
                        <p className={`stat-value text-2xl ${
                          w.last_temp < 131
                            ? "text-red-600 dark:text-red-400"
                            : "text-forest-600 dark:text-forest-400"
                        }`}>
                          {w.last_temp}°
                        </p>
                        <p className="text-[10px] text-muted-foreground">last temp</p>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground/50">No data</p>
                    )}
                  </div>
                  <ChevronRight size={18} className="text-muted-foreground/30" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Quick log buttons */}
      <div className="grid grid-cols-2 gap-3 pt-2">
        <Link
          to="/app/operator/log-temp"
          className="flex items-center justify-center gap-2 py-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-xl text-sm font-semibold text-amber-700 dark:text-amber-400 active:scale-[0.97] transition-transform"
        >
          <Flame size={18} />
          Log Temp
        </Link>
        <Link
          to="/app/operator/log-turn"
          className="flex items-center justify-center gap-2 py-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl text-sm font-semibold text-blue-700 dark:text-blue-400 active:scale-[0.97] transition-transform"
        >
          <Thermometer size={18} />
          Log Turn
        </Link>
      </div>
    </div>
  );
}
